import { useState, useEffect, useCallback } from 'react';
import { DeviceDefinition } from '../deviceDefinitions';

const STORAGE_KEY = 'smilebooth-custom-displays';

export interface CustomDisplayConfig {
    id: string;
    name: string;
    pixels: {
        width: number;
        height: number;
    };
    scale: number;
    points: {
        width: number;
        height: number;
    };
}

export default function useCustomDisplays() {
    const [customDisplays, setCustomDisplays] = useState<DeviceDefinition[]>(
        [],
    );

    // Load custom displays from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    setCustomDisplays(parsed);
                }
            }
        } catch (error) {
            console.error('Error loading custom displays:', error);
        }
    }, []);

    // Save to localStorage whenever custom displays change
    const saveToStorage = useCallback((displays: DeviceDefinition[]) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(displays));
        } catch (error) {
            console.error('Error saving custom displays:', error);
        }
    }, []);

    const addCustomDisplay = useCallback(
        (config: CustomDisplayConfig) => {
            const newDisplay: DeviceDefinition = {
                ...config,
                category: 'Display',
            };
            const updated = [...customDisplays, newDisplay];
            setCustomDisplays(updated);
            saveToStorage(updated);
        },
        [customDisplays, saveToStorage],
    );

    const updateCustomDisplay = useCallback(
        (id: string, config: Partial<CustomDisplayConfig>) => {
            const updated = customDisplays.map((display) =>
                display.id === id ? { ...display, ...config } : display,
            );
            setCustomDisplays(updated);
            saveToStorage(updated);
        },
        [customDisplays, saveToStorage],
    );

    const deleteCustomDisplay = useCallback(
        (id: string) => {
            const updated = customDisplays.filter(
                (display) => display.id !== id,
            );
            setCustomDisplays(updated);
            saveToStorage(updated);
        },
        [customDisplays, saveToStorage],
    );

    const getCustomDisplay = useCallback(
        (id: string) => {
            return customDisplays.find((display) => display.id === id);
        },
        [customDisplays],
    );

    return {
        customDisplays,
        addCustomDisplay,
        updateCustomDisplay,
        deleteCustomDisplay,
        getCustomDisplay,
    };
}

